// @flow
import React, { Component, Fragment } from "react";
import classNames from "classnames";
import Drawer from "@material-ui/core/Drawer";
import List from "@material-ui/core/List";
import Typography from "@material-ui/core/Typography";
import IconButton from "@material-ui/core/IconButton";
import Hidden from "@material-ui/core/Hidden";
import MenuIcon from "@material-ui/icons/Menu";
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";
import { withStyles } from "@material-ui/core/styles";

import type { DictionaryType } from "../../types/GeneralTypes";
import type { IconStylesClassesType } from "../../styles/iconStyles";
import { iconStyles } from "../../styles/iconStyles";

const NavBarWidth = 248;
const NavBarCloseWidth = 64;

const styles = (theme: DictionaryType): DictionaryType => ({
  ...iconStyles(theme),
  root: {
    display: "block",
    margin: "0 auto",
    width: "100%"
  },
  drawer: {
    width: NavBarWidth,
    height: "100%",
    flexShrink: 0,
    position: "fixed",
    zIndex: 2
  },
  drawerClose: {
    width: NavBarCloseWidth
  },
  drawerPaper: {
    width: NavBarWidth,
    backgroundColor: theme.palette.primary.light,
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen
    })
  },
  drawerPaperClose: {
    overflowX: "hidden",
    width: NavBarCloseWidth,
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen
    })
  },
  drawerHeader: {
    display: "flex",
    alignItems: "center",
    padding: "0 8px",
    ...theme.mixins.toolbar,
    justifyContent: "flex-end"
  },
  header: {
    width: "100%",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center"
  },
  logo: {
    width: "38px",
    height: "38px"
  },
  hide: {
    display: "none !IMPORTANT"
  },
  title: {
    lineHeight: "38px",
    color: theme.palette.primary.contrastText,
    marginLeft: "8px"
  },
  wrap: {
    width: "auto",
    height: "100%",
    marginLeft: NavBarWidth + theme.spacing.unit * 2,
    marginRight: theme.spacing.unit * 2,
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen
    }),
    [theme.breakpoints.down("sm")]: {
      marginLeft: theme.spacing.unit * 1,
      marginRight: theme.spacing.unit * 1
    }
  },
  wrapClose: {
    marginLeft: NavBarCloseWidth + theme.spacing.unit * 2,
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen
    }),
    [theme.breakpoints.down("sm")]: {
      marginLeft: theme.spacing.unit * 1,
      marginRight: theme.spacing.unit * 1
    }
  }
});

type NavbarMenuClassesType = {
  ...IconStylesClassesType,
  root: DictionaryType,
  drawer: DictionaryType,
  drawerClose: DictionaryType,
  drawerPaper: DictionaryType,
  drawerPaperClose: DictionaryType,
  drawerHeader: DictionaryType,
  header: DictionaryType,
  logo: DictionaryType,
  hide: DictionaryType,
  title: DictionaryType,
  wrap: DictionaryType,
  wrapClose: DictionaryType
};

type NavbarMenuPropsType = {
  classes: *,
  children: React$Node,
  menus: React$Node,
  logo: React$Node,
  title: string
};

type NavbarMenuStateType = {
  open: boolean,
  isHover: boolean
};

class NavbarMenu extends Component<NavbarMenuPropsType, NavbarMenuStateType> {
  constructor(props: NavbarMenuPropsType) {
    super(props);
    this.state = {
      open: window.innerWidth > 1400 ? true : false,
      isHover: false
    };
  }

  handleDrawerOpen = (status: boolean): * => (): * => {
    this.setState({ open: status });
  };

  handleDrawerHover = (status: boolean): * => (): * => {
    if (!this.state.open) {
      this.setState({ isHover: status });
    }
  };

  render(): React$Element<*> {
    const classes = this.props.classes;

    return (
      <div className={classes.root}>
        <Hidden smDown>
          <Drawer
            className={classNames(classes.drawer, {
              [classes.drawerClose]: !this.state.open
            })}
            variant="permanent"
            anchor="left"
            open={this.state.open}
            classes={{
              paper: classNames(classes.drawerPaper, {
                [classes.drawerPaperClose]:
                  !this.state.open && !this.state.isHover
              })
            }}
          >
            <div className={classes.drawerHeader}>
              <div
                className={classNames(classes.header, {
                  [classes.hide]: !this.state.open && !this.state.isHover
                })}
              >
                <img
                  alt={this.props.title}
                  src={this.props.logo}
                  className={classes.logo}
                />
                <Typography variant="subtitle1" className={classes.title}>
                  {this.props.title}
                </Typography>
              </div>
              {this.state.open ? (
                <IconButton onClick={this.handleDrawerOpen(false)}>
                  <ChevronLeftIcon className={classes.lightIcon} />
                </IconButton>
              ) : (
                <IconButton onClick={this.handleDrawerOpen(true)}>
                  <MenuIcon className={classes.lightIcon} />
                </IconButton>
              )}
            </div>
            <List
              onMouseEnter={this.handleDrawerHover(true)}
              onMouseLeave={this.handleDrawerHover(false)}
            >
              {this.props.menus}
            </List>
          </Drawer>
        </Hidden>
        <main
          className={classNames(classes.wrap, {
            [classes.wrapClose]: !this.state.open
          })}
        >
          {this.props.children}
        </main>
      </div>
    );
  }
}

export default withStyles(styles)(NavbarMenu);
